import { WHEEL_SETTINGS } from "./wheel-config.js";

/**
 * Classe principale per la Ruota della Fortuna
 */
export class WheelOfFortune {
    constructor(config) {
        this.segments = config.segments || [];
        this.settings = { ...WHEEL_SETTINGS, ...config.settings };

        // Elementi DOM
        this.wheelElement = document.getElementById("wheel");

        // Stato interno
        this.isSpinning = false;
        this.currentRotation = 0;

        // Validazione e inizializzazione
        this.validateConfig();
        this.init();
    }

    /**
     * Valida la configurazione fornita
     */
    validateConfig() {
        if (!this.wheelElement) {
            throw new Error("Elementi DOM richiesti non trovati. Assicurati che esistano elementi con id: wheel");
        }

        if (!Array.isArray(this.segments) || this.segments.length === 0) {
            throw new Error("Configurazione segmenti non valida");
        }

        console.log(`Ruota inizializzata con ${this.segments.length} segmenti`);
    }

    /**
     * Inizializza la ruota
     */
    init() {
        this.createSegments();
        this.setInitialPosition();
    }

    /**
     * Imposta la posizione iniziale della ruota
     */
    setInitialPosition() {
        const segmentAngle = 360 / this.segments.length;
        this.currentRotation = -(segmentAngle / 2);
        this.wheelElement.style.transform = `rotate(${this.currentRotation}deg)`;
    }

    /**
     * Crea i segmenti SVG della ruota
     */
    createSegments() {
        this.wheelElement.innerHTML = "";
        const segmentAngle = 360 / this.segments.length;

        // Crea SVG container
        const svg = this.createSVGContainer();

        this.segments.forEach((segment, index) => {
            // Crea il segmento SVG
            const path = this.createSegmentPath(segment, index, segmentAngle);
            svg.appendChild(path);

            // Crea il testo del segmento
            const textContainer = this.createSegmentText(segment, index, segmentAngle);
            this.wheelElement.appendChild(textContainer);
        });

        this.wheelElement.appendChild(svg);
    }

    /**
     * Crea il container SVG
     */
    createSVGContainer() {
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("width", "100%");
        svg.setAttribute("height", "100%");
        svg.setAttribute("viewBox", "0 0 500 500");
        svg.style.position = "absolute";
        svg.style.top = "0";
        svg.style.left = "0";
        svg.style.zIndex = "1"; // Sotto i testi
        return svg;
    }

    /**
     * Crea un singolo segmento SVG
     */
    createSegmentPath(segment, index, segmentAngle) {
        const { WHEEL_RADIUS, WHEEL_CENTER_X, WHEEL_CENTER_Y } = this.settings;

        const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
        const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);

        // Calcola i punti del segmento
        const x1 = WHEEL_CENTER_X + WHEEL_RADIUS * Math.cos(startAngle);
        const y1 = WHEEL_CENTER_Y + WHEEL_RADIUS * Math.sin(startAngle);
        const x2 = WHEEL_CENTER_X + WHEEL_RADIUS * Math.cos(endAngle);
        const y2 = WHEEL_CENTER_Y + WHEEL_RADIUS * Math.sin(endAngle);

        // Crea il path SVG
        const largeArcFlag = segmentAngle > 180 ? 1 : 0;
        const pathData = [`M ${WHEEL_CENTER_X} ${WHEEL_CENTER_Y}`, `L ${x1} ${y1}`, `A ${WHEEL_RADIUS} ${WHEEL_RADIUS} 0 ${largeArcFlag} 1 ${x2} ${y2}`, "Z"].join(" ");

        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", pathData);
        path.setAttribute("fill", segment.color);

        return path;
    }

    /**
     * Crea il testo per un segmento
     */
    createSegmentText(segment, index, segmentAngle) {
        const { WHEEL_RADIUS, WHEEL_CENTER_X, WHEEL_CENTER_Y, TEXT_RADIUS_RATIO } = this.settings;

        const textAngle = (index * segmentAngle + segmentAngle / 2 - 90) * (Math.PI / 180);
        const textRadius = WHEEL_RADIUS * TEXT_RADIUS_RATIO;
        const textX = WHEEL_CENTER_X + textRadius * Math.cos(textAngle);
        const textY = WHEEL_CENTER_Y + textRadius * Math.sin(textAngle);

        const textContainer = document.createElement("div");
        textContainer.style.position = "absolute";
        textContainer.style.top = "0";
        textContainer.style.left = "0";
        textContainer.style.width = "100%";
        textContainer.style.height = "100%";
        textContainer.style.pointerEvents = "none";
        textContainer.style.zIndex = "10"; // Sopra l'SVG

        const textElement = document.createElement("div");
        textElement.style.position = "absolute";
        textElement.style.left = `${(textX / 500) * 100}%`;
        textElement.style.top = `${(textY / 500) * 100}%`;
        // Testo orizzontale orientato verso il centro della ruota
        // Rotazione di +90° per rendere il testo orizzontale con l'icona verso il centro
        const textRotation = index * segmentAngle + segmentAngle / 2;

        textElement.style.transform = `translate(-50%, -50%) rotate(${textRotation}deg)`;
        textElement.classList.add("wheel__text");

        // Applica gli stili direttamente
        //textElement.style.color = "white";
        //textElement.style.fontWeight = "bold";
        //textElement.style.textAlign = "center";
        //textElement.style.fontSize = "clamp(10px, 2.5vw, 16px)";
        //textElement.style.textShadow = "1px 1px 2px rgba(0, 0, 0, 0.5)";
        //textElement.style.lineHeight = "1.2";
        //textElement.style.whiteSpace = "nowrap";

        textElement.innerHTML = `
            <div class="wheel__text-message">${segment.text}</div>
            <div class="wheel__text-icon">${segment.icon}</div>
        `;

        textContainer.appendChild(textElement);
        return textContainer;
    }

    /**
     * Esegue la rotazione della ruota
     */
    spin(targetSegmentIndex = null) {
        if (this.isSpinning) return;

        this.isSpinning = true;

        // Determina il segmento target
        if (targetSegmentIndex === null) {
            targetSegmentIndex = Math.floor(Math.random() * this.segments.length);
        }

        // Calcola la rotazione
        const finalRotation = this.calculateFinalRotation(targetSegmentIndex);

        // Applica l'animazione
        this.wheelElement.style.transform = `rotate(${finalRotation}deg)`;
        this.currentRotation = finalRotation;

        // Gestisce il risultato dopo l'animazione
        setTimeout(() => {
            this.handleSpinComplete(targetSegmentIndex);
        }, this.settings.SPIN_DURATION);
    }

    /**
     * Calcola la rotazione finale per centrare il segmento target
     */
    calculateFinalRotation(targetSegmentIndex) {
        const segmentAngle = 360 / this.segments.length;
        const targetAngle = -(targetSegmentIndex * segmentAngle + segmentAngle / 2);
        const rotationAmount = this.settings.SPIN_ROTATIONS * 360;

        return this.currentRotation + rotationAmount + (targetAngle - (this.currentRotation % 360));
    }

    /**
     * Gestisce il completamento della rotazione
     */
    handleSpinComplete(segmentIndex) {
        this.isSpinning = false;

        // Dispatch evento personalizzato con il risultato
        window.dispatchEvent(
            new CustomEvent("wheelSpinComplete", {
                detail: {
                    segmentIndex: segmentIndex,
                    segment: this.segments[segmentIndex],
                    isWinning: this.segments[segmentIndex].isWinning,
                },
            })
        );
    }

    /**
     * API pubblica: gira verso un segmento specifico
     */
    spinToSegment(segmentIndex) {
        if (segmentIndex < 0 || segmentIndex >= this.segments.length) {
            console.error(`Indice segmento non valido: ${segmentIndex}. Deve essere tra 0 e ${this.segments.length - 1}`);
            return;
        }
        this.spin(segmentIndex);
    }

    /**
     * API pubblica: aggiorna i segmenti
     */
    updateSegments(newSegments) {
        if (!Array.isArray(newSegments) || newSegments.length === 0) {
            console.error("Segmenti non validi");
            return;
        }

        this.segments = newSegments;
        this.createSegments();
        this.setInitialPosition();
        console.log(`Segmenti aggiornati: ${newSegments.length} segmenti`);
    }

    /**
     * API pubblica: ottieni il segmento attualmente selezionato
     */
    getCurrentSegment() {
        const segmentAngle = 360 / this.segments.length;
        const normalizedRotation = ((this.currentRotation % 360) + 360) % 360;
        const segmentIndex = Math.floor(normalizedRotation / segmentAngle);
        return {
            index: segmentIndex,
            segment: this.segments[segmentIndex],
        };
    }

    /**
     * API pubblica: ottieni informazioni sulla ruota
     */
    getWheelInfo() {
        return {
            segmentCount: this.segments.length,
            isSpinning: this.isSpinning,
            currentRotation: this.currentRotation,
            settings: this.settings,
        };
    }
}
